// The roadmap's card-layout arithmetic, pure and testable: how wide a week is on this
// screen, which cards are too narrow to carry their own name, where the name goes instead,
// and how cards pack into lanes without anything — card, record slot or floating name —
// landing on top of anything else. RoadmapView.tsx renders what this module decides.
//
// THE LAW OF THE CALENDAR: it fits the screen. Week width is measured off the actual
// scrollport, not picked from a zoom tier — so the board fills its sheet exactly whenever
// the span allows, and the ⤢ breakout (or a window resize) re-scales the weeks instead of
// exposing more empty scrollport. Only below MIN_WEEK_PX does the grid overflow into the
// compass-panned scroll.
//
// THE LAW OF THE SHORT CARD: the bar tells the truth, and stands as tall as its neighbours.
// A card whose drawn span is narrower than COMPACT_PX keeps its exact span (the edges ARE
// the dates, and the stretch handles stay on them) but renders slim: ref on top if there is
// room, the name WRAPPED INSIDE and clamped. There is no floating tail any more — a name
// hanging outside its card shared pixels with pinned neighbours (pins may overlap by the
// law of the still board) and with whatever painted later, so the title hid exactly when
// two short cards sat adjacent. Inside the border nothing can cover it, and the footprint
// a card reserves is honest again: drawn span ∪ record slot, nothing invisible.

/** Below this, a week column stops being a usable drop target and the grid overflows. */
export const MIN_WEEK_PX = 44;
/** Below this drawn width, a card switches to the slim wrapped-title form. */
export const COMPACT_PX = 168;
/** Inside a bar narrower than this, the ref row does not fit — the tooltip carries it. */
export const REF_MIN_PX = 60;

/** The measured fit: weeks share the scrollport (minus the grid's own padding), floored. */
export function fitWeekPx(scrollportPx: number, gridPaddingPx: number, totalWeeks: number): number {
  if (totalWeeks <= 0) return MIN_WEEK_PX;
  return Math.max(MIN_WEEK_PX, Math.floor((scrollportPx - gridPaddingPx) / totalWeeks));
}

export interface CardSpan {
  id: number;
  /** drawn start week (inclusive) and end week (inclusive) */
  sw: number;
  ew: number;
  /** the record's own slot when the drawn card is an assumption standing elsewhere */
  slot: { sw: number; ew: number } | null;
  /** the deliverable's name — rides for the renderer; the layout math no longer prices it */
  name: string;
  /** drawn as an assumption — rides for the renderer */
  ghost: boolean;
  /**
   * THE LAW OF THE STILL BOARD: a lane the operator PINNED this card to
   * (deliverables.roadmap_lane, or the session's sticky memory of an auto-packed card).
   * A pinned card takes exactly that lane — even where it overlaps, because the operator
   * placed it and only the operator moves it — and packing flows the rest AROUND it.
   * null/undefined = pack it.
   */
  pin?: number | null;
}

export interface CardLayout<T extends CardSpan> {
  card: T;
  lane: number;
  /** the slim wrapped-title form */
  compact: boolean;
  /** in compact form, whether the ref row still fits inside */
  refInside: boolean;
}

/** The full lane footprint of a laid-out card: drawn span ∪ record slot. */
export function footprintOf<T extends CardSpan>(l: CardLayout<T>, _weekPx: number): { from: number; to: number } {
  return {
    from: Math.min(l.card.sw, l.card.slot?.sw ?? l.card.sw),
    to: Math.max(l.card.ew, l.card.slot?.ew ?? l.card.ew),
  };
}

/**
 * Packs cards into lanes, first-fit by start week, exactly as the board draws them —
 * including the record slot an assumption leaves behind. Input order should be the draw
 * order (by start, then end).
 */
export function layoutCards<T extends CardSpan>(cards: T[], weekPx: number, _totalWeeks: number): CardLayout<T>[] {
  const laid: CardLayout<T>[] = cards.map((card) => {
    const spanPx = (card.ew - card.sw + 1) * weekPx;
    return { card, lane: 0, compact: spanPx < COMPACT_PX, refInside: spanPx >= REF_MIN_PX };
  });
  // First-fit packing over the FULL footprint: drawn span ∪ slot.
  // Pinned cards go first and go EXACTLY where they were put; their footprints become the
  // obstacles the floating cards then pack around. The lanes array may be sparse (a pin in
  // lane 4 over an empty 2) — a floater happily takes the hole.
  const lanes: Array<{ from: number; to: number }[]> = [];
  for (const l of laid) {
    if (l.card.pin === null || l.card.pin === undefined) continue;
    l.lane = Math.max(0, Math.floor(l.card.pin));
    (lanes[l.lane] ??= []).push(footprintOf(l, weekPx));
  }
  for (const l of laid) {
    if (l.card.pin !== null && l.card.pin !== undefined) continue;
    const { from, to } = footprintOf(l, weekPx);
    let i = 0;
    while (i < lanes.length && (lanes[i] ?? []).some((o) => o.from <= to && from <= o.to)) i += 1;
    (lanes[i] ??= []).push({ from, to });
    l.lane = i;
  }
  return laid;
}
